import React, { useEffect, useState } from "react";
import {
  Box,
  Flex,
  Text,
  Icon,
  useColorModeValue,
  VStack,
  Button,
  Divider,
  Badge,
  useToast,
  Image,
  SimpleGrid,
} from "@chakra-ui/react";
import { MdArrowBack, MdLocationOn, MdEvStation } from "react-icons/md";
import ccstype2 from "assets/img/chargePointsTab/ccstype2.png";
import AC1 from "assets/img/chargePointsTab/actype1.png";
import AC2 from "assets/img/chargePointsTab/ac2.png";
import CDMO from "assets/img/chargePointsTab/chademo.png";
import DOMESTIC_D from "assets/img/chargePointsTab/domestic_d.png";
import useAxios from "axios-hooks";
import { useNavigate, useParams } from "react-router-dom";
import LoadingSpinner from "components/feedback/LoadingSpinner";
import OngoingSession from "./OngoingSession";

const ViewActiveSession = () => {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const [sessionData, setSessionData] = useState(null);
  const [stationData, setStationData] = useState(null);
  const [connector, setConnector] = useState(null);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const toast = useToast();

  const bgColor = useColorModeValue("gray.50", "gray.900");
  const cardBg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const textColor = useColorModeValue("gray.800", "white");
  const labelColor = useColorModeValue("gray.600", "gray.400");

  const [{ data: sessionResponse, loading: sessionLoading }, getSession] =
    useAxios(
      { method: "GET", url: `/api/chargingSession/${sessionId}` },
      { manual: true }
    );

  const [{ data: stationResponse, loading: stationLoading }, getStation] =
    useAxios({ method: "GET" }, { manual: true });

  const [{ loading: stopLoading }, executeStop] = useAxios(
    { method: "POST", url: `/api/ocpp16/charging/stop` },
    { manual: true }
  );

  const fetchSessionData = async () => {
    try {
      const response = await getSession();
      const session = response?.data?.data;
      setSessionData(session);

      // Fetch station details
      if (!stationData) {
        try {
          const stationRes = await getStation({
            url: `/api/evses/forStation/${session.ChargingStationId}`,
          });
          const evseAllStationData = stationRes?.data?.data;
          const evseData = evseAllStationData?.find(
            (evse) => evse.EvseSerialId === session.ConnectorId
          );
          setStationData(evseData);
          setConnector(evseData?.Connectors?.[0]);
        } catch (error) {
          console.error("Error fetching station:", error);
        }
      }

      if (isInitialLoad) {
        setIsInitialLoad(false);
      }
    } catch (error) {
      console.error("Error fetching session data:", error);
      // ERR code is cancled return
      if (error?.code === "ERR_CANCELED") {
        return;
      }
      toast({
        title: "Error",
        description: "Failed to load session details",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  useEffect(() => {
    if (sessionId) {
      fetchSessionData();
    }
  }, [sessionId]);

  // Poll for session data every 5 seconds
  useEffect(() => {
    if (sessionId && sessionData?.Status !== "Completed") {
      const interval = setInterval(fetchSessionData, 10000);
      return () => clearInterval(interval);
    }
  }, [sessionId, sessionData?.Status]);

  const handleStopCharging = async () => {
    if (!sessionData) return;

    const body = {
      StationId: sessionData.EvseId,
      TransactionId: sessionData.Id,
    };

    try {
      await executeStop({ data: body });
      toast({
        title: "Session Stopped",
        description: "Charging session stopped successfully",
        status: "success",
        duration: 5000,
        isClosable: true,
      });
      // Navigate to session summary after stopping
      setTimeout(() => {
        navigate(`/admin/charging_management/summary/${sessionData.Id}`);
      }, 1500);
    } catch (error) {
      toast({
        title: "Error Stopping Session",
        description: error.response?.data?.error || "Something went wrong",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const calculateDuration = () => {
    if (!sessionData?.StartTime) return "0h 0m";

    const startTime = new Date(sessionData.StartTime);
    const now = new Date();
    const diffMs = now - startTime;
    const diffSeconds = Math.floor(diffMs / 1000);

    const hours = Math.floor(diffSeconds / 3600);
    const minutes = Math.floor((diffSeconds % 3600) / 60);

    return `${hours}h ${minutes}m`;
  };

  const getConnectorImage = (connectorType) => {
    switch (connectorType) {
      case "AC Type 1":
        return { img: AC1, name: "AC Type 1" };
      case "AC Type 2":
        return { img: AC2, name: "AC Type 2" };
      case "CCS Type 2":
        return { img: ccstype2, name: "CCS Type 2" };
      case "CHAdeMO":
        return { img: CDMO, name: "CHAdeMO" };
      case "DOMESTIC_D":
        return { img: DOMESTIC_D, name: "3 Pin Socket (16A - 230V)" };
      default:
        return { img: ccstype2, name: connectorType || "CCS Type 2" };
    }
  };

  if (sessionLoading && isInitialLoad) {
    return (
      <Box pt={{ base: 40, md: 10 }}>
        <LoadingSpinner />
      </Box>
    );
  }

  if (!sessionData) {
    return (
      <Box bg={bgColor} pt={{ base: 40, md: 10 }} px={{ base: 2, md: 4 }}>
        <Text>Session not found</Text>
      </Box>
    );
  }

  const connectorType = connector?.Standard;
  const connectorInfo = getConnectorImage(connectorType);

  return (
    <Box bg={bgColor} pt={{ base: 40, md: 10 }} px={{ base: 2, md: 4 }} pb={6}>
      <VStack spacing={6} align="stretch">
        {/* Back Button */}
        <Flex justify="flex-start">
          <Button
            leftIcon={<MdArrowBack />}
            bgColor="brand.500"
            _hover={{ bgColor: "#d44e00" }}
            onClick={() =>
              navigate("/admin/charging_management/active-sessions")
            }
            color="white"
            px={{ base: 4, md: 5 }}
            py={{ base: 2, md: 3 }}
            fontSize={{ base: "sm", md: "md" }}
            borderRadius={8}
          >
            Back
          </Button>
        </Flex>

        {/* Location & Charger Details Card */}
        <Box
          bg={cardBg}
          borderRadius="xl"
          border="1px solid"
          borderColor={borderColor}
          p={{ base: 3, md: 6 }}
          boxShadow="sm"
        >
          <VStack align="stretch" spacing={{ base: 3, md: 4 }}>
            {/* Charger Name */}
            <Flex
              align="center"
              gap={{ base: 2, md: 3 }}
              justify="space-between"
            >
              <Flex align="center" gap={{ base: 2, md: 3 }}>
                <Icon
                  as={MdEvStation}
                  boxSize={{ base: 5, md: 6 }}
                  color="brand.500"
                />
                <VStack align="start" spacing={0}>
                  <Text
                    fontSize={{ base: "2xs", md: "xs" }}
                    color={labelColor}
                    fontWeight="semibold"
                  >
                    CHARGER
                  </Text>
                  <Text
                    fontSize={{ base: "md", md: "lg", lg: "xl" }}
                    fontWeight="bold"
                    color={textColor}
                  >
                    {stationData?.Name ||
                      sessionData?.StationName ||
                      sessionData?.EvseId ||
                      "N/A"}
                  </Text>
                </VStack>
              </Flex>
            </Flex>

            <Divider />

            {/* Power Type & Power Row */}
            <Flex gap={{ base: 2, md: 3 }}>
              {/* Power Type */}
              <Box
                p={{ base: 2, md: 3 }}
                bg="rgba(35, 169, 121, 0.05)"
                borderRadius="lg"
                border="1px solid"
                borderColor="rgba(35, 169, 121, 0.2)"
                flex="1"
              >
                <VStack align="start" spacing={0.5}>
                  <Text
                    fontSize={{ base: "2xs", md: "xs" }}
                    color={labelColor}
                    fontWeight="semibold"
                  >
                    POWER TYPE
                  </Text>
                  <Text
                    fontSize={{ base: "sm", md: "md" }}
                    fontWeight="bold"
                    color={textColor}
                  >
                    {connector?.PowerType || "NA"}
                  </Text>
                </VStack>
              </Box>

              {/* Power */}
              <Box
                p={{ base: 2, md: 3 }}
                bg="rgba(35, 169, 121, 0.05)"
                borderRadius="lg"
                border="1px solid"
                borderColor="rgba(35, 169, 121, 0.2)"
                flex="1"
              >
                <VStack align="start" spacing={0.5}>
                  <Text
                    fontSize={{ base: "2xs", md: "xs" }}
                    color={labelColor}
                    fontWeight="semibold"
                  >
                    POWER
                  </Text>
                  <Text
                    fontSize={{ base: "sm", md: "md" }}
                    fontWeight="bold"
                    color={textColor}
                  >
                    {connector?.MaxElectricPower || "0"} kW
                  </Text>
                </VStack>
              </Box>
            </Flex>

            {/* Connector Row - Full Width */}
            <Box
              p={{ base: 2, md: 3 }}
              bg="rgba(35, 169, 121, 0.05)"
              borderRadius="lg"
              border="1px solid"
              borderColor="rgba(35, 169, 121, 0.2)"
            >
              <Flex align="center" gap={2}>
                <Box
                  p={1}
                  borderRadius="md"
                  border="1px solid"
                  borderColor="rgba(35, 169, 121, 0.3)"
                  bg="white"
                  flexShrink={0}
                >
                  <Image
                    src={connectorInfo.img}
                    alt={connectorInfo.name}
                    w={{ base: "30px", md: "40px" }}
                    h={{ base: "30px", md: "40px" }}
                    objectFit="contain"
                  />
                </Box>
                <VStack align="start" spacing={0}>
                  <Text
                    fontSize={{ base: "2xs", md: "xs" }}
                    color={labelColor}
                    fontWeight="semibold"
                  >
                    Gun {sessionData?.ConnectorId}
                  </Text>
                  <Text
                    fontSize={{ base: "xs", md: "sm" }}
                    fontWeight="bold"
                    color={textColor}
                  >
                    {connectorInfo.name}
                  </Text>
                </VStack>
              </Flex>
            </Box>
          </VStack>
        </Box>

        {/* Ongoing Session Component */}
        <OngoingSession
          sessionData={sessionData}
          station={stationData}
          selectedConnector={connector}
          handleStopCharging={handleStopCharging}
          stopLoading={stopLoading}
          calculateDuration={calculateDuration}
        />
      </VStack>
    </Box>
  );
};

export default ViewActiveSession;
